import React, { FC, useCallback, useMemo, useState } from 'react';
import i18next from 'i18next';
import { observer } from 'mobx-react';
import { useQuery } from '@apollo/client';
import { DEFAULT_ALERT_FILTER_CATEGORY_TECHNICAL_LIST } from 'core/constants';
import { DataTableStateEvent } from 'primereact/datatable';
import {
  GqlAnomalyAlertsPaginated,
  GqlPlotAlertsPaginated,
  GqlPlotSensorsSystems,
} from 'gql/types/plots';
import { GET_PLOTS_ANOMALY_ALERTS_PAGINATED } from 'gql/queries/plots';
import { AlertSeverity, AlertState, AlertVariant } from 'models/alert';
import { useStores } from 'stores/hooks/hooks';
import { GrowerType } from 'models/grower';
import { subDays } from 'date-fns';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  handleCreateAnomalyData,
  TABLE_ALERTS_MAX_ROWS_NUMBER,
} from '../AlertsTable.utils';
import { useAlertsTableFilters } from '../hooks/useAlertsTableFilters';
import AnomalyAlertsTable from './AnomalyAlertsTable';
import MenuBar from './MenuBar';

const StyledPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledAlertsContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const MenuBarContainer = styled.div`
  padding-left: 1rem;
`;

// eslint-disable-next-line
// @ts-ignore
const defaultLazyParams: DataTableStateEvent = {
  first: 0,
  page: 0,
  rows: TABLE_ALERTS_MAX_ROWS_NUMBER,
};

const AnomalyAlertsTab: FC = observer(() => {
  const { resellersStore, snackBarStore } = useStores();
  const { t: alertTableT } = useTranslation('liveMonitoring', {
    keyPrefix: 'anomalyAlertsTable',
  });

  const [lazyParams, setLazyParams] =
    useState<DataTableStateEvent>(defaultLazyParams);

  const { alertFilters, debouncedFilters, onAlertFilterChange } =
    useAlertsTableFilters();

  const growerIds = resellersStore.selectedTreeNodeCurrentDepthGrowerIds;
  const isGrower = resellersStore.selectedGrowerType === GrowerType.Grower;

  const querySeverity = isGrower
    ? [AlertSeverity.High, AlertSeverity.Medium, AlertSeverity.Low]
    : [AlertSeverity.High];

  const fromDate = useMemo(() => {
    const currentDate = new Date();
    const oneWeekBefore = subDays(currentDate, 7);
    return oneWeekBefore.toISOString();
  }, []);

  const { data: plotAnomalyAlerts, loading: isPlotAnomalyAlertsLoading } =
    useQuery<GqlAnomalyAlertsPaginated>(GET_PLOTS_ANOMALY_ALERTS_PAGINATED, {
      variables: {
        growerIds: [resellersStore.selectedGrowerId],
        alertPage: lazyParams.page,
        alertSize: lazyParams.rows,
        urgencies: querySeverity,
        subTree: true,
        from: fromDate, // ISOString format , example: '2024-01-01T00:00:00Z',
        ...debouncedFilters,
      },
      fetchPolicy: 'no-cache',
      skip: !growerIds.length,
      onError() {
        snackBarStore.showToast({
          detail: i18next.t('errors:something_went_wrong'),
        });
      },
    });

  const downloadAllQuery = {
    query: GET_PLOTS_ANOMALY_ALERTS_PAGINATED,
    variables: {
      growerIds: [resellersStore.selectedGrowerId],
      urgencies: querySeverity,
      subTree: true,
      from: fromDate, // ISOString format , example: '2024-01-01T00:00:00Z',
    },
  };

  const csvFields = useMemo(() => {
    const fields = [];
    if (!isGrower) {
      fields.push({
        fieldKey: 'growerName',
        name: alertTableT('grower_name'),
      });
    }

    fields.push({ fieldKey: 'plotName', name: alertTableT('plot_name') });
    fields.push({
      fieldKey: 'description',
      name: alertTableT('description'),
    });

    fields.push({ fieldKey: 'sensorName', name: alertTableT('sensor_name') });
    fields.push({ fieldKey: 'sensorType', name: alertTableT('sensor_type') });
    fields.push({
      fieldKey: 'sensorSerial',
      name: alertTableT('sensor_serial'),
    });

    fields.push({
      fieldKey: 'systemSerial',
      name: alertTableT('system_serial'),
    });

    fields.push({ fieldKey: 'urgency', name: alertTableT('urgency') });
    fields.push({
      fieldKey: 'sensorDetails',
      name: alertTableT('sensor_details'),
    });

    fields.push({
      fieldKey: 'fromTimestamp',
      name: alertTableT('from_timestamp'),
    });

    fields.push({ fieldKey: 'alertCount', name: alertTableT('alert_count') });
    return fields;
  }, [isGrower, alertTableT]);

  const onPageChange = useCallback(
    (params: DataTableStateEvent) => {
      if (params.page !== lazyParams.page) {
        setLazyParams(params);
      }
    },
    [lazyParams],
  );

  const anomalyAlertsData = useMemo(() => {
    return handleCreateAnomalyData(plotAnomalyAlerts);
  }, [plotAnomalyAlerts]);

  return (
    <StyledPageContainer>
      <MenuBarContainer>
        <MenuBar
          query={downloadAllQuery}
          fields={csvFields}
          tableType="anomaly"
        />
      </MenuBarContainer>
      <StyledAlertsContainer>
        <AnomalyAlertsTable
          isReseller={!isGrower}
          alerts={anomalyAlertsData as GqlAnomalyAlertsPaginated}
          tableParams={lazyParams}
          loading={isPlotAnomalyAlertsLoading}
          onPageChange={onPageChange}
          alertFilters={alertFilters}
          onAlertFilterChange={onAlertFilterChange}
          variant={AlertVariant.Technical}
        />
      </StyledAlertsContainer>
    </StyledPageContainer>
  );
});

export default AnomalyAlertsTab;
